import { useEffect, useState } from 'react';
import { Loader2, MapPin, MapPinOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import type { GeoCoords, GeoStatus } from '@/hooks/useGeolocation';

interface GpsStatusCardProps {
  status: GeoStatus;
  coords: GeoCoords | null;
  errorMessage: string | null;
  onRefresh: () => void;
}

/**
 * Renders the current GPS state alongside coords + last-captured age.
 * Always visible on the Clock page so users see *why* the action button
 * might be disabled.
 */
export function GpsStatusCard({ status, coords, errorMessage, onRefresh }: GpsStatusCardProps) {
  const isOk = status === 'granted' && coords !== null;
  const isPending = status === 'requesting' || status === 'idle';

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        isOk
          ? 'border-brand-success/30 bg-brand-success/5'
          : isPending
            ? 'border-brand-blue/30 bg-brand-blue/5'
            : 'border-brand-danger/30 bg-brand-danger/5',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-brand-blue" aria-hidden />
          ) : isOk ? (
            <MapPin className="h-5 w-5 text-brand-success" aria-hidden />
          ) : (
            <MapPinOff className="h-5 w-5 text-brand-danger" aria-hidden />
          )}
        </div>
        <div className="flex-1 text-sm">
          {isPending && <p className="font-medium text-brand-charcoal">Requesting location…</p>}
          {isOk && coords && (
            <>
              <p className="font-medium text-brand-charcoal">Location captured</p>
              <p className="mt-1 text-xs text-brand-gray-mid">
                <span className="font-mono">
                  {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
                </span>
                <span className="mx-1.5">·</span>
                ±{Math.round(coords.accuracy)}m
                <span className="mx-1.5">·</span>
                <RelativeAge iso={coords.capturedAt} />
              </p>
            </>
          )}
          {!isOk && !isPending && (
            <>
              <p className="font-medium text-brand-danger">
                {status === 'denied' ? 'Location access denied' : 'Location unavailable'}
              </p>
              <p className="mt-1 text-xs text-brand-charcoal/80">
                {errorMessage}
              </p>
            </>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isPending}
          aria-label="Refresh location"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isPending && 'animate-spin')} aria-hidden />
        </Button>
      </div>

      {status === 'denied' && (
        <Alert tone="warning" className="mt-3">
          To enable: click the lock icon in the browser address bar, then allow Location. GPS is
          required for every clock event.
        </Alert>
      )}
    </div>
  );
}

function RelativeAge({ iso }: { iso: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 5) return <>just now</>;
  if (seconds < 60) return <>{seconds}s ago</>;
  if (seconds < 3600) return <>{Math.floor(seconds / 60)}m ago</>;
  return <>{Math.floor(seconds / 3600)}h ago</>;
}
